import { useState, useCallback } from "react";
import * as XLSX from "xlsx";

const sanitizeHeaderName = (name) => {
  if (name === null || name === undefined) return "";
  return String(name).trim().replace(/\s+/g, "_");
};

export const useFileUploadModel = (uploadFile, uploadFileToDb) => {
  const [tableName, setTableName] = useState("NewTable");
  const [description, setDescription] = useState("");
  const [fileType, setFileType] = useState("Excel (.xlsx, .xls)");
  const [dataLocation, setDataLocation] = useState("Local Drive");
  const [selectedFile, setSelectedFile] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState("");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [excelHeaders, setExcelHeaders] = useState([]);
  const [tableHeaders, setTableHeaders] = useState([]);
  const [currentStep, setCurrentStep] = useState(1);

  // New states for enhanced flow
  const [tableOption, setTableOption] = useState(""); // "new" or "existing"
  const [previewData, setPreviewData] = useState([]);
  const [columnTypes, setColumnTypes] = useState({});
  const [editableHeaders, setEditableHeaders] = useState({});
  const [showPreview, setShowPreview] = useState(false);
  const [uploadResponse, setUploadResponse] = useState(null); // Store upload response
  const [isProcessing, setIsProcessing] = useState(false); // Processing state for next step
  const [originalHeaders, setOriginalHeaders] = useState([]);
  const [dateFormatPrompt, setDateFormatPrompt] = useState(null); // { columns: [{header, detectedFormats}] }
  const [dateOutputFormats, setDateOutputFormats] = useState({}); // column header -> output format

  // States for existing table flow
  const [existingTableSchema, setExistingTableSchema] = useState(null);
  const [headerMappings, setHeaderMappings] = useState({}); // Maps excel column index to existing table column name
  const [showHeaderMapping, setShowHeaderMapping] = useState(false);
  const [fetchingSchema, setFetchingSchema] = useState(false);
  // Data type detection helper
  const detectDataType = useCallback((value) => {
    if (value === null || value === undefined || value === "") return "text";

    // Check if it's a number
    if (!isNaN(value) && !isNaN(parseFloat(value))) {
      const num = parseFloat(value);
      const str = value.toString();
      if (str.includes(".") || str.toLowerCase().includes("e")) {
        return "decimal";
      }
      return Number.isInteger(num) ? "integer" : "decimal";
    }

    // Check if it's a date
    const dateValue = new Date(value);
    if (
      !isNaN(dateValue.getTime()) &&
      value.toString().match(/\d{1,4}[-\/]\d{1,2}[-\/]\d{1,4}/)
    ) {
      return "date";
    }

    // Check if it's boolean
    if (
      typeof value === "boolean" ||
      value.toString().toLowerCase() === "true" ||
      value.toString().toLowerCase() === "false"
    ) {
      return "boolean";
    }

    return "text";
  }, []);

  const MAX_PREVIEW_SIZE = 1 * 1024 * 1024; // 1MB
  const MAX_PREVIEW_LINES = 1000;

  const parseFile = useCallback(
    async (file) => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: "array" });

            setSheets(workbook.SheetNames);
            if (workbook.SheetNames.length > 0) {
              const firstSheet = workbook.SheetNames[0];
              setSelectedSheet(firstSheet);
              parseSheetData(workbook, firstSheet);
            }
            resolve();
          } catch (error) {
            let message = error?.message || "Unable to read file for preview.";
            if (message.includes("Bad compressed size")) {
              message =
                "Failed to open Excel file for preview. The file may be too large or not a valid .xlsx file.";
            }
            setError(message);
            console.error("File parsing error:", error);
            resolve();
          }
        };
        const name = file.name?.toLowerCase() || "";
        const ext = name.substring(name.lastIndexOf("."));
        const isTextLike = ext === ".csv" || ext === ".txt";
        if (isTextLike && file.size > MAX_PREVIEW_SIZE) {
          const blob = file.slice(0, MAX_PREVIEW_SIZE);
          reader.readAsArrayBuffer(blob);
        } else {
          reader.readAsArrayBuffer(file);
        }
      });
    },
    [detectDataType]
  );

  const parseSheetData = useCallback(
    (workbook, sheetName) => {
      try {
        const worksheet = workbook.Sheets[sheetName];
        let jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        if (jsonData.length === 0) return;

        const rawHeaders = (jsonData[0] || []).map((header, index) =>
          header !== undefined && header !== null
            ? header
            : `Column_${index + 1}`
        );
        const dataRows = jsonData.slice(1, 11);
        const sampleRows = jsonData.slice(1, 1 + MAX_PREVIEW_LINES);

        setOriginalHeaders(rawHeaders);

        const sanitizedHeaders = rawHeaders.map((header) =>
          sanitizeHeaderName(header)
        );

        setExcelHeaders(sanitizedHeaders);
        setPreviewData(dataRows);

        const editableHeadersObj = {};
        sanitizedHeaders.forEach((header, index) => {
          editableHeadersObj[index] = header || `Column_${index + 1}`;
        });
        setEditableHeaders(editableHeadersObj);

        // Detect column types
        const types = {};
        rawHeaders.forEach((header, colIndex) => {
          const columnData = dataRows
            .map((row) => row[colIndex])
            .filter((val) => val !== undefined && val !== null && val !== "");

          if (columnData.length === 0) {
            types[colIndex] = "text";
            return;
          }

          // Get most common type in the column
          const typeCount = {};
          columnData.forEach((value) => {
            const type = detectDataType(value);
            typeCount[type] = (typeCount[type] || 0) + 1;
          });

          let finalType = Object.keys(typeCount).reduce((a, b) =>
            typeCount[a] > typeCount[b] ? a : b
          );

          if (typeCount["decimal"] && typeCount["integer"]) {
            finalType = "decimal";
          }

          types[colIndex] = finalType;
        });

        setColumnTypes(types);
      } catch (error) {
        setError(`Error parsing sheet data: ${error.message}`);
        console.error("Sheet parsing error:", error);
      }
    },
    [detectDataType]
  );

  const handleFileSelect = useCallback(
    async (file) => {
      if (!file) return;

      // Validate file type - only allow CSV, TXT, and XLSX files
      const allowedExtensions = ['.csv', '.txt', '.xlsx'];
      const fileName = file.name.toLowerCase();
      const fileExtension = fileName.substring(fileName.lastIndexOf('.'));

      if (!allowedExtensions.includes(fileExtension)) {
        setError(`Invalid file type. Only CSV, TXT, and Excel (.xlsx) files are allowed.`);
        return;
      }

      setSelectedFile(file);
      setUploading(true);
      setError(null);

      try {
        const uploadResult = await uploadFile(file);
        if (!uploadResult.success) {
          throw new Error(uploadResult.error);
        }

        // Store the upload response for later use
        setUploadResponse(uploadResult.data);
        await parseFile(file);
      } catch (error) {
        setError(`Upload failed: Network Error. Please Try Again`);
        console.error("File upload error:", error);
      } finally {
        setUploading(false);
      }
    },
    [uploadFile, parseFile]
  );

  const selectSheet = useCallback(
    (sheetName) => {
      setSelectedSheet(sheetName);
      if (!selectedFile) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          parseSheetData(workbook, sheetName);
        } catch (error) {
          setError(`Error reading sheet: ${error.message}`);
          console.error("Sheet reading error:", error);
        }
      };
      reader.readAsArrayBuffer(selectedFile);
    },
    [selectedFile, parseSheetData]
  );

  const handleTableOptionChange = useCallback((option) => {
    setTableOption(option);
    if (option === "new") {
      setShowPreview(true);
      setShowHeaderMapping(false);
    } else if (option === "existing") {
      // For existing table: show Excel preview, hide mapping UI until Next is clicked
      setShowPreview(true);
      setShowHeaderMapping(false);
    }
  }, []);

  const updateHeaderName = useCallback((columnIndex, newName) => {
    const safeName = sanitizeHeaderName(newName);
    setEditableHeaders((prev) => ({
      ...prev,
      [columnIndex]: safeName,
    }));
  }, []);

  const updateColumnType = useCallback((columnIndex, newType) => {
    setColumnTypes((prev) => ({
      ...prev,
      [columnIndex]: newType,
    }));
  }, []);

  // Function to fetch existing table schema

  // Auto-map headers based on name similarity
  const autoMapHeaders = useCallback(
    (existingColumns) => {
      const mappings = {};
      const existingColumnNames = existingColumns.map((col) =>
        col.name.toLowerCase()
      );

      excelHeaders.forEach((excelHeader, index) => {
        const excelHeaderLower = excelHeader.toLowerCase();

        // Exact match
        let matchedColumn = existingColumnNames.find(
          (existing) => existing === excelHeaderLower
        );

        // Partial match (contains)
        if (!matchedColumn) {
          matchedColumn = existingColumnNames.find(
            (existing) =>
              existing.includes(excelHeaderLower) ||
              excelHeaderLower.includes(existing)
          );
        }

        // Fuzzy match (similarity)
        if (!matchedColumn) {
          matchedColumn = existingColumnNames.find((existing) => {
            const similarity = calculateSimilarity(excelHeaderLower, existing);
            return similarity > 0.7; // 70% similarity threshold
          });
        }

        if (matchedColumn) {
          const originalColumn = existingColumns.find(
            (col) => col.name.toLowerCase() === matchedColumn
          );
          mappings[index] = originalColumn.name;
        }
      });

      setHeaderMappings(mappings);
    },
    [excelHeaders]
  );

  // Calculate string similarity (Levenshtein distance based)
  const calculateSimilarity = useCallback((str1, str2) => {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;

    if (longer.length === 0) return 1.0;

    const distance = levenshteinDistance(longer, shorter);
    return (longer.length - distance) / longer.length;
  }, []);

  const levenshteinDistance = useCallback((str1, str2) => {
    const matrix = [];

    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }

    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }

    return matrix[str2.length][str1.length];
  }, []);

  // Update header mapping
  const updateHeaderMapping = useCallback(
    (excelColumnIndex, existingColumnName) => {
      setHeaderMappings((prev) => ({
        ...prev,
        [excelColumnIndex]: existingColumnName,
      }));
    },
    []
  );

  const handleDataLocationChange = useCallback((location) => {
    setDataLocation(location);
    if (location !== "Local Drive") {
      setSelectedFile(null);
      setSheets([]);
      setSelectedSheet("");
      setExcelHeaders([]);
      setError(null);
    }
  }, []);

  const canProceedToNextStep = useCallback(() => {
    switch (currentStep) {
      case 1:
        const basicRequirements =
          selectedFile !== null &&
          !uploading &&
          selectedSheet !== "" &&
          tableOption !== "";

        // console.log("canProceedToNextStep debug:", {
        //   selectedFile: !!selectedFile,
        //   uploading,
        //   selectedSheet,
        //   tableOption,
        //   tableName,
        //   existingTableSchema: !!existingTableSchema,
        //   headerMappings: Object.keys(headerMappings),
        //   headerMappingsLength: Object.keys(headerMappings).length,
        //   basicRequirements,
        // });

        if (tableOption === "new") {
          return basicRequirements && tableName.trim() !== "";
        } else if (tableOption === "existing") {
          const result =
            basicRequirements &&
            tableName.trim() !== "" &&
            existingTableSchema &&
            Object.keys(headerMappings).length > 0;
          // console.log("Existing table validation result:", result);
          return result;
        }

        return basicRequirements;
      default:
        return false;
    }
  }, [
    currentStep,
    selectedFile,
    uploading,
    selectedSheet,
    tableOption,
    tableName,
    existingTableSchema,
    headerMappings,
  ]);

  const nextStep = useCallback(async () => {
    if (!canProceedToNextStep()) return false;

    setIsProcessing(true);
    setError(null);

    try {
      let schemaJson;

      if (tableOption === "new") {
        schemaJson = {
          columns: excelHeaders.map((header, index) => ({
            header: editableHeaders[index] || header,
            originalHeader: originalHeaders[index] || header,
            type: columnTypes[index] || "VARCHAR",
            dateOutputFormat:
              (dateOutputFormats[editableHeaders[index] || header] || "").trim() ||
              undefined,
          })),
        };
      } else if (tableOption === "existing") {
        // For existing table, create schema based on header mappings
        schemaJson = {
          columns: Object.entries(headerMappings).map(
            ([excelIndex, existingColumnName]) => {
              const existingColumn = existingTableSchema.columns.find(
                (col) => col.name === existingColumnName
              );
              const index = parseInt(excelIndex, 10);
              return {
                header: existingColumnName,
                originalHeader:
                  originalHeaders[index] || excelHeaders[index] || "",
                type: existingColumn
                  ? mapPostgresTypeToSchema(existingColumn.dataType)
                  : "VARCHAR",
                dateOutputFormat:
                  (dateOutputFormats[existingColumnName] || "").trim() || undefined,
              };
            }
          ),
          headerMappings: headerMappings, // Include the header mappings for the backend
        };
      }

      // Call UploadFileToDb API with the correct schema format
      const appendMode = tableOption === "existing";
      // console.log(
      //   "📋 Frontend: Uploading to",
      //   appendMode ? "existing" : "new",
      //   "table:",
      //   tableName
      // );
      // console.log("📋 Frontend: append mode:", appendMode);
      // console.log(
      //   "📋 Frontend: schemaJson:",
      //   JSON.stringify(schemaJson, null, 2)
      // );

      const result = await uploadFileToDb(
        tableName, // table name
        uploadResponse?.object_name || uploadResponse?.objectName, // object name from upload response
        schemaJson, // schema JSON - contains columns array
        appendMode, // append: true for existing, false for new
        selectedSheet // selected sheet name
      );
      // console.log("UploadFileToDb result:", result);
      if (result.success) {
        // console.log("UploadFileToDb success:", result);
        // Success - move to next step or show success message
        setDateFormatPrompt(null);
        setCurrentStep((prev) => prev + 1);
        // console.log("File uploaded to database successfully:", result.data);
        return true;
      } else {
        // Extract GraphQL error message if present

        let errorMessage = result.error || "Failed to upload file to database";

        // Check if error is a GraphQL error object
        if (
          result.errors &&
          Array.isArray(result.errors) &&
          result.errors.length > 0
        ) {
          errorMessage = result.errors[0].message;
        }

        throw new Error(errorMessage);
      }
    } catch (error) {
      // Extract error message from GraphQL response if present
      let errorMessage = error.message;

      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        errorMessage = error.graphQLErrors[0].message;
      } else if (
        error.networkError &&
        error.networkError.result &&
        error.networkError.result.errors
      ) {
        errorMessage = error.networkError.result.errors[0].message;
      }

      if (
        typeof errorMessage === "string" &&
        errorMessage.startsWith("DATE_FORMATS::")
      ) {
        try {
          const payload = JSON.parse(errorMessage.replace("DATE_FORMATS::", ""));
          if (payload && Array.isArray(payload.columns)) {
            setDateFormatPrompt(payload);
            const formatsList = payload.columns
              .flatMap((c) =>
                Array.isArray(c?.detectedFormats) ? c.detectedFormats : []
              )
              .filter(Boolean);
            const uniqueFormats = Array.from(new Set(formatsList));
            setError(
              `Detected more than 1 date format (${uniqueFormats.join(", ")}). ` +
                `Please enter one format to enforce and retry.`
            );
          } else {
            setError(errorMessage);
          }
        } catch {
          setError(errorMessage);
        }
      } else {
        // Keep any existing date-format prompt visible so the user can correct input
        // (e.g., invalid output format token errors).
        setError(errorMessage);
      }
      console.error("Database upload error:", error);
      return false;
    } finally {
      setIsProcessing(false);
    }
  }, [
    canProceedToNextStep,
    excelHeaders,
    originalHeaders,
    editableHeaders,
    columnTypes,
    tableName,
    uploadResponse,
    uploadFileToDb,
    tableOption,
    selectedSheet,
    existingTableSchema,
    headerMappings,
    dateOutputFormats,
  ]);

  const setDateOutputFormat = useCallback((header, format) => {
    setDateOutputFormats((prev) => ({
      ...prev,
      [header]: format,
    }));
  }, []);

  // Helper function to map PostgreSQL types to schema types
  const mapPostgresTypeToSchema = useCallback((postgresType) => {
    const typeMap = {
      varchar: "text",
      text: "text",
      "character varying": "text",
      integer: "integer",
      int: "integer",
      bigint: "integer",
      smallint: "integer",
      numeric: "decimal",
      decimal: "decimal",
      real: "decimal",
      "double precision": "decimal",
      float: "decimal",
      boolean: "boolean",
      bool: "boolean",
      date: "date",
      timestamp: "date",
      "timestamp with time zone": "date",
      "timestamp without time zone": "date",
      time: "date",
    };

    return typeMap[postgresType.toLowerCase()] || "text";
  }, []);

  const previousStep = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  }, [currentStep]);

  const reset = useCallback(() => {
    setTableName("NewTable");
    setDescription("");
    setFileType("Excel (.xlsx, .xls)");
    setDataLocation("Local Drive");
    setSelectedFile(null);
    setSheets([]);
    setSelectedSheet("");
    setUploading(false);
    setError(null);
    setExcelHeaders([]);
    setTableHeaders([]);
    setCurrentStep(1);
    setTableOption("");
    setPreviewData([]);
    setColumnTypes({});
    setEditableHeaders({});
    setShowPreview(false);
    setUploadResponse(null);
    setIsProcessing(false);
    // Reset existing table flow states
    setExistingTableSchema(null);
    setHeaderMappings({});
    setShowHeaderMapping(false);
    setFetchingSchema(false);
  }, []);

  const trySample = useCallback(async () => {
    console.log("Try sample functionality - to be implemented");
  }, []);

  return {
    tableName,
    description,
    fileType,
    dataLocation,
    selectedFile,
    sheets,
    selectedSheet,
    uploading,
    error,
    excelHeaders,
    tableHeaders,
    currentStep,
    tableOption,
    previewData,
    columnTypes,
    editableHeaders,
    showPreview,
    uploadResponse,
    isProcessing,
    dateFormatPrompt,
    dateOutputFormats,
    // Existing table flow states
    existingTableSchema,
    headerMappings,
    showHeaderMapping,
    fetchingSchema,
    setTableName,
    setDescription,
    setFileType,
    setDataLocation: handleDataLocationChange,
    handleFileSelect,
    selectSheet,
    handleTableOptionChange,
    updateHeaderName,
    updateColumnType,
    nextStep,
    previousStep,
    reset,
    trySample,
    canProceedToNextStep,
    // Existing table flow functions
    updateHeaderMapping,
    setShowPreview,
    setExistingTableSchema,
    autoMapHeaders,
    setDateOutputFormat,
    setDateFormatPrompt,
  };
};
